import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiviTimeline from "../eleves/SuiviTimeline";
import EmailModal from "../../components/EmailModal";
import api from "../../services/api";
import { formatPlanning } from "utils/planning";
import { useTranslation } from "react-i18next";
import { libelleMatiere } from "utils/valeurs";

function GroupeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [groupe, setGroupe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [emailModal, setEmailModal] = useState(false);

  useEffect(() => {
    api.get(`/groupes/${id}`)
      .then(({ data }) => {
        setGroupe(data);
        if (data.eleves?.length > 0) setSelectedEleve(data.eleves[0]);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(t("groupes.confirmerSuppression"))) return;
    try {
      await api.delete(`/groupes/${id}`);
      navigate("/groupes");
    } catch (error) {
      alert(error.response?.data?.message || t("commun.erreurSuppression"));
    }
  };

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6} display="flex" justifyContent="center">
        <MDTypography variant="body2" color="text">{t("commun.chargement")}</MDTypography>
      </MDBox>
    </DashboardLayout>
  );

  if (!groupe) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6}>
        <MDTypography variant="body2" color="error">{t("groupes.introuvable")}</MDTypography>
      </MDBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={10}>

            {/* Actions */}
            <MDBox mb={3} display="flex" alignItems="center"
              justifyContent="space-between" flexWrap="wrap" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/groupes")} sx={{ borderRadius: "10px", px: 2 }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t("commun.retour")}</MDBox>
              </MDButton>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton variant="outlined" color="info" size="small"
                  onClick={() => setEmailModal(true)} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>email</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    {t("groupes.emailGroupe")}
                  </MDBox>
                </MDButton>
                <MDButton variant="outlined" color="warning" size="small"
                  onClick={() => navigate(`/groupes/${id}/modifier`)} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>edit</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t("commun.modifier")}</MDBox>
                </MDButton>
                <MDButton variant="outlined" color="error" size="small"
                  onClick={handleDelete} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>cancel</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t("commun.supprimer")}</MDBox>
                </MDButton>
                <MDButton variant="contained" size="small"
                  onClick={() => navigate(`/cours/nouveau?groupe=${id}`)}
                  sx={{
                    background: "linear-gradient(135deg, #00695c, #00897b)",
                    color: "#fff", borderRadius: "10px", fontWeight: 700,
                  }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>add</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    {t("cours.planifier")}
                  </MDBox>
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Header groupe */}
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", mb: 3 }}>
              <MDBox mx={2} mt={-3} py={3} px={{ xs: 2, sm: 3 }} sx={{
                background: "linear-gradient(135deg, #00695c, #00897b)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,105,92,0.4)",
              }}>
                <MDBox display="flex" alignItems="center" gap={2}>
                  <MDBox sx={{
                    width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, borderRadius: "12px",
                    background: "rgba(255,255,255,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon sx={{ color: "#fff", fontSize: { xs: 22, sm: 28 } }}>groups</Icon>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h5" color="white" fontWeight="bold"
                      sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
                      {groupe.nom}
                    </MDTypography>
                    <MDBox display="flex" gap={1} alignItems="center">
                      <MDBox sx={{
                        padding: "2px 10px", borderRadius: "10px",
                        background: "rgba(255,255,255,0.2)", color: "#fff",
                        fontSize: 11, fontWeight: 600,
                      }}>
                        {groupe.type === "permanent" ? `🔄 ${t("valeurs.typeGroupe.permanent")}` : `⚡ ${t("valeurs.typeGroupe.ponctuel")}`}
                      </MDBox>
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                        {t("cours.nbEleves", { count: groupe.eleves?.length || 0 })}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox pt={3} pb={3} px={{ xs: 2, sm: 3 }}>
                {groupe.description && (
                  <MDTypography variant="body2" color="text" mb={3} sx={{ fontStyle: "italic" }}>
                    {groupe.description}
                  </MDTypography>
                )}

                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 2,
                }}>
                  {t("cours.form.elevesGroupe")}
                </MDTypography>
                <Grid container spacing={1.5}>
                  {(groupe.eleves || []).map((eleve) => (
                    <Grid item xs={12} sm={6} md={4} key={eleve.id}>
                      <MDBox display="flex" alignItems="center" gap={1.5} py={1.5} px={2}
                        sx={{
                          borderRadius: "12px", cursor: "pointer",
                          border: "1px solid",
                          borderColor: selectedEleve?.id === eleve.id ? "#00695c" : "#f0f0f0",
                          background: selectedEleve?.id === eleve.id ? "#e0f2f1" : "#f8f8f8",
                          transition: "all 0.2s",
                          "&:hover": { background: "#e0f2f1", borderColor: "#00695c" },
                        }}
                        onClick={() => setSelectedEleve(eleve)}>
                        <MDBox sx={{
                          width: 38, height: 38, borderRadius: "50%",
                          background: selectedEleve?.id === eleve.id ? "#00695c" : "#e0e0e0",
                          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 13, flexShrink: 0, overflow: "hidden",
                        }}>
                          {eleve.photo ? (
                            <img src={eleve.photo} alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : `${eleve.prenom?.[0] || ""}${eleve.nom?.[0] || ""}`}
                        </MDBox>
                        <MDBox flex={1}>
                          <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#344767" }}>
                            {eleve.prenom} {eleve.nom}
                          </MDTypography>
                          {eleve.ville && (
                            <MDTypography variant="caption" color="text" display="block">
                              📍 {eleve.ville}
                            </MDTypography>
                          )}
                        </MDBox>
                        <MDBox onClick={(e) => { e.stopPropagation(); navigate(`/eleves/${eleve.id}`); }}
                          sx={{
                            width: 28, height: 28, borderRadius: "8px",
                            background: "#e3f2fd", color: "#1565c0",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", "&:hover": { background: "#bbdefb" },
                          }}>
                          <Icon sx={{ fontSize: 14 }}>visibility</Icon>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>

                {(groupe.cours || []).length > 0 && (
                  <MDBox mt={3}>
                    <Divider />
                    <MDTypography variant="caption" fontWeight="bold" sx={{
                      textTransform: "uppercase", fontSize: 11,
                      letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 2, mt: 2,
                    }}>
                      {t("groupes.coursPlanifies")}
                    </MDTypography>
                    {groupe.cours.map((cours) => (
                      <MDBox key={cours.id} onClick={() => navigate(`/cours/${cours.id}`)}
                        display="flex" alignItems="center" gap={2} py={1.5} px={2} mb={1}
                        sx={{
                          borderRadius: "10px", cursor: "pointer",
                          background: "#f8f8f8", border: "1px solid #f0f0f0",
                          transition: "all 0.2s",
                          "&:hover": { background: "#e0f2f1", borderColor: "#00695c33" },
                        }}>
                        <MDBox sx={{
                          width: 36, height: 36, borderRadius: "10px",
                          background: "#e0f2f1", color: "#00695c",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <Icon sx={{ fontSize: 18 }}>menu_book</Icon>
                        </MDBox>
                        <MDBox flex={1}>
                          <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                            {libelleMatiere(cours.matiere)}
                          </MDTypography>
                          {cours.enseignant && (
                            <MDTypography variant="caption" color="text" display="block">
                              👨‍🏫 {cours.enseignant.prenom} {cours.enseignant.nom}
                              {formatPlanning(cours) && ` · 🕐 ${formatPlanning(cours)}`}
                            </MDTypography>
                          )}
                        </MDBox>
                        <Icon sx={{ color: "#00695c", fontSize: 18 }}>arrow_forward</Icon>
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            </Card>

            {/* Suivi par élève */}
            {selectedEleve && (
              <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <MDBox mx={2} mt={-3} py={2.5} px={{ xs: 2, sm: 3 }} sx={{
                  background: "linear-gradient(135deg, #1565c0, #1976d2)",
                  borderRadius: "12px", boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
                }}>
                  <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ color: "#fff", fontSize: 22 }}>timeline</Icon>
                    <MDBox>
                      <MDTypography variant="h6" color="white" fontWeight="bold">
                        {t("eleves.sections.suiviChapitres")}
                      </MDTypography>
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                        {selectedEleve.prenom} {selectedEleve.nom}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
                <MDBox pt={3} pb={3} px={{ xs: 2, sm: 3 }}>
                  <MDBox display="flex" gap={1} flexWrap="wrap" mb={3}>
                    {(groupe.eleves || []).map((eleve) => (
                      <MDBox key={eleve.id} onClick={() => setSelectedEleve(eleve)} sx={{
                        padding: "6px 16px", borderRadius: "20px", cursor: "pointer",
                        border: "1px solid",
                        borderColor: selectedEleve?.id === eleve.id ? "#1565c0" : "#d2d6da",
                        background: selectedEleve?.id === eleve.id ? "#1565c0" : "#fff",
                        color: selectedEleve?.id === eleve.id ? "#fff" : "#344767",
                        fontSize: 13, fontWeight: 500, transition: "all 0.2s",
                      }}>
                        {eleve.prenom} {eleve.nom}
                      </MDBox>
                    ))}
                  </MDBox>
                  <SuiviTimeline eleveId={selectedEleve.id} />
                </MDBox>
              </Card>
            )}

          </Grid>
        </Grid>
      </MDBox>

      <EmailModal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        groupeId={groupe.id}
        nomDestinataire={t("groupes.emailTous", { nom: groupe.nom })}
        elevesGroupe={groupe.eleves}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default GroupeDetail;